import React from "react";
import { Workflow } from "lucide-react";
import { Modal } from "./Modal";
import { SubcontractorTypeahead } from "./SubcontractorTypeahead";
import api, { apiErr } from "../lib/api";
import { toast } from "sonner";

/**
 * SPT-PTE-2026-0021: bulk "Send to Subcon" from Active Lab / Job Detail's
 * bulk action toolbar — lab staff can redirect one or more selected line
 * items straight to a subcontractor without asking admin to make the
 * change manually. Deliberately minimal per the client's request ("nothing
 * fancy with dates or so on") — just pick the subcontractor and send.
 */
export function SendToSubconModal({ items, onClose, onDone }) {
  const [subcontractorName, setSubcontractorName] = React.useState("");
  const [note, setNote] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  const valid = subcontractorName.trim().length > 0;

  const confirm = async () => {
    if (!valid) { toast.error("Please select a subcontractor."); return; }
    setBusy(true);
    try {
      const r = await api.post("/job-items/bulk-subcontractor", {
        item_ids: items.map((i) => i.id),
        subcontractor_name: subcontractorName.trim(),
        notes: note.trim() || undefined,
      });
      toast.success(`${r.data.count} line${r.data.count !== 1 ? "s" : ""} sent to ${subcontractorName.trim()}`);
      if (r.data.skipped?.length) toast.message(`${r.data.skipped.length} skipped — ${r.data.skipped[0].reason}`);
      onDone && onDone();
      onClose && onClose();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} icon={Workflow}
      title="Send to Subcontractor"
      subtitle={`${items.length} line${items.length !== 1 ? "s" : ""} selected`}
      footer={<>
        <button className="btn ghost" onClick={onClose} disabled={busy} data-testid="send-subcon-cancel">Cancel</button>
        <button className="btn gold" onClick={confirm} disabled={!valid || busy} data-testid="send-subcon-confirm">
          <Workflow size={13} /> {busy ? "Sending…" : "Send to Subcon"}
        </button>
      </>}>

      <div className="notice" style={{ marginBottom: 12 }}>
        <span>
          Moves the selected line{items.length !== 1 ? "s" : ""} into "To send" on the Subcontractor
          page, and off the current lab/technician assignment.
        </span>
      </div>

      <div style={{ marginBottom: 10 }}>
        <div className="t-mut" style={{ fontSize: 11.5, marginBottom: 4 }}>Selected line{items.length !== 1 ? "s" : ""}:</div>
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5 }}>
          {items.map((it) => (
            <li key={it.id}>
              {it.description || "—"}
              {it.serial_number ? ` · SN ${it.serial_number}` : it.issue_number ? ` · Issue ${it.issue_number}` : ""}
            </li>
          ))}
        </ul>
      </div>

      <label className="t-mut" style={{ fontSize: 11.5, fontWeight: 600, display: "block", marginBottom: 4 }}>
        Subcontractor *
      </label>
      <SubcontractorTypeahead
        value={subcontractorName} onChange={setSubcontractorName}
        placeholder="Start typing — pick from history or enter new"
        testid="send-subcon-name" />
      <div style={{ marginBottom: 12 }} />

      <label className="t-mut" style={{ fontSize: 11.5, fontWeight: 600, display: "block", marginBottom: 4 }}>
        Note (optional)
      </label>
      <input type="text" placeholder="e.g. Capacity backlog — sending out for calibration"
        value={note} onChange={(e) => setNote(e.target.value)}
        data-testid="send-subcon-note"
        style={{ width: "100%" }} />
    </Modal>
  );
}
